import { auth, db } from "../firebase";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  increment,
  or,
  query,
  runTransaction,
  updateDoc,
  where,
} from "firebase/firestore";
import { tinhTrangThaiTiepTheo, TRANG_THAI_KHOA } from "../lib/trangThaiSot";
import { langNgheFirestore } from "../lib/langNgheFirestore";
import { lichSuKhoApi } from "./lichSuKho";

// Các field thanh toán được phép ghi qua luồng kế toán — khớp hasOnly([...])
// nhánh (1)+(2) trong firestore.rules match /giaoDich. Số liệu gốc (items…)
// đi nhánh riêng (suaPO), không nằm đây.
const FIELD_THANH_TOAN = [
  "trangThaiThanhToan",
  "nguoiXuLyThanhToan",
  "thoiGianThanhToan",
  "ghiChuThanhToan",
  "chungTuUrl",
  "nguoiNhanThongTin",
  "thoiGianNhanThongTin",
];

const TRANG_THAI_HANG_CHO_KE_TOAN = ["cho_ke_toan", "dang_thanh_toan"];
const COOLDOWN_CHUONG_MS = 30_000;

/** Kiểm tra cooldown chuông (đồng bộ) — dùng trước khi phát tiếng. */
export function kiemTraDaChuong(thoiGianChuongKeToan) {
  const lanTruoc = thoiGianChuongKeToan ? Date.parse(thoiGianChuongKeToan) : 0;
  if (Number.isFinite(lanTruoc) && Date.now() - lanTruoc < COOLDOWN_CHUONG_MS) {
    const con = Math.ceil((COOLDOWN_CHUONG_MS - (Date.now() - lanTruoc)) / 1000);
    return { ok: false, conGiay: con, message: `Đợi ${con} giây nữa mới chuông lại được` };
  }
  return { ok: true, conGiay: 0, message: "" };
}

const giaoDichCol = () => collection(db, "giaoDich");

function tongKhoiLuong(items) {
  return items.reduce((s, it) => s + Number(it.khoiLuong), 0);
}

function kiemTraDongItems(items) {
  for (const it of items) {
    const kl = Number(it.khoiLuong);
    const dg = Number(it.donGia);
    if (!it.maLoaiBo || !it.maSot) {
      throw new Error("Thiếu thông tin giao dịch");
    }
    if (!Number.isFinite(kl) || kl <= 0) {
      throw new Error("Khối lượng mỗi dòng phải lớn hơn 0");
    }
    if (!Number.isFinite(dg) || dg <= 0) {
      throw new Error("Đơn giá mỗi dòng phải lớn hơn 0");
    }
  }
}

function tinhItems(items) {
  return items.map((it) => {
    const khoiLuong = Number(it.khoiLuong);
    const donGia = Number(it.donGia);
    return {
      maLoaiBo: it.maLoaiBo,
      khoiLuong,
      donGia,
      // VND nguyên — tránh 12.3*20000 thành 245999.999… lúc copy số tiền.
      thanhTien: Math.round(khoiLuong * donGia),
      maSot: it.maSot,
    };
  });
}

function chuanVuotToiDa(sot, khoiLuongMoi) {
  const toiDa = sot.khoiLuongToiDa;
  if (toiDa == null || !Number.isFinite(Number(toiDa))) return;
  if (khoiLuongMoi > Number(toiDa) + 1e-9) {
    const conTrong = Math.max(0, Number(toiDa) - (sot.khoiLuongHienTai ?? 0));
    throw new Error(
      `Sọt ${sot.maSot ?? ""} chỉ còn trống ${conTrong} kg (tối đa ${sot.khoiLuongToiDa} kg)`
    );
  }
}

// Gom items theo Sọt mà item đó thuộc về — dùng ở taoMoi/suaPO/xoaPO để
// tính delta khối lượng riêng cho TỪNG Sọt khi 1 PO trải nhiều Sọt, thay
// vì giả định cả PO chỉ chạm đúng 1 Sọt như trước.
function gomTheoSot(items) {
  const map = new Map();
  items.forEach((it) => {
    if (!map.has(it.maSot)) map.set(it.maSot, []);
    map.get(it.maSot).push(it);
  });
  return map;
}

// Document cũ (trước khi gộp nhiều loại bơ vào 1 PO) lưu thẳng
// maLoaiBo/khoiLuong/donGia/thanhTien ở gốc, không có "items". Bọc lại thành
// PO 1 dòng để mọi nơi đọc chỉ cần biết duy nhất 1 hình dạng "PO có items[]"
// — không migrate/xoá dữ liệu cũ, chỉ tương thích ngược khi đọc.
//
// Tương tự, doc cũ hơn nữa (trước khi PO có thể trải nhiều Sọt) có "items"
// nhưng từng item KHÔNG có maSot/chuKyId riêng — cả PO khi đó luôn chỉ
// gắn đúng 1 Sọt (field top-level gd.maSot/gd.chuKyId), nên item thiếu
// field nào thì lấy field top-level tương ứng làm giá trị. Luôn chạy qua
// bước này (không early-return khi đã có items[]) để backfill đúng cho mọi
// hình dạng doc cũ.
function chuanHoaItems(gd) {
  const maSotGoc = gd.maSot;
  const itemsGoc = Array.isArray(gd.items)
    ? gd.items
    : [
        {
          maLoaiBo: gd.maLoaiBo,
          khoiLuong: gd.khoiLuong,
          donGia: gd.donGia,
          thanhTien: gd.thanhTien,
        },
      ];
  const items = itemsGoc.map((it) => ({
    ...it,
    maSot: it.maSot ?? maSotGoc,
    chuKyId: it.chuKyId ?? gd.chuKyId,
  }));
  return {
    ...gd,
    maSot: maSotGoc,
    items,
    danhSachSot: gd.danhSachSot ?? [...new Set(items.map((it) => it.maSot).filter(Boolean))],
    danhSachChuKy: gd.danhSachChuKy ?? [...new Set(items.map((it) => it.chuKyId).filter(Boolean))],
  };
}

async function ganTenLienQuan(dsGiaoDichTho, { kemNhanVien = false } = {}) {
  const dsGiaoDich = dsGiaoDichTho.map(chuanHoaItems);

  const maChuVuonList = [...new Set(dsGiaoDich.map((gd) => gd.maChuVuon).filter(Boolean))];
  const maVuonList = [...new Set(dsGiaoDich.map((gd) => gd.maVuon).filter(Boolean))];
  const maLoaiBoList = [
    ...new Set(dsGiaoDich.flatMap((gd) => gd.items.map((it) => it.maLoaiBo)).filter(Boolean)),
  ];
  // giaoDich.maSot (top-level) và mỗi item.maSot lưu ID document Firestore
  // của Sọt (xem giaoDichApi.taoMoi), không phải mã hiển thị "LV…"/cũ —
  // phải tra ngược sang doc Sọt để lấy đúng mã đồng bộ với trang Quản lý
  // Sọt. Gom từ CẢ field top-level LẪN mọi item vì PO có thể trải nhiều Sọt
  // khác nhau.
  const maSotList = [
    ...new Set(dsGiaoDich.flatMap((gd) => [gd.maSot, ...(gd.danhSachSot ?? [])]).filter(Boolean)),
  ];
  const maNhanVienList = kemNhanVien
    ? [...new Set(dsGiaoDich.map((gd) => gd.maNhanVien).filter(Boolean))]
    : [];
  const maHopTacXaList = [...new Set(dsGiaoDich.map((gd) => gd.maHopTacXa).filter(Boolean))];

  const [chuVuonDocs, vuonDocs, loaiBoDocs, sotDocs, nhanVienDocs, hopTacXaDocs] =
    await Promise.all([
      Promise.all(maChuVuonList.map((id) => getDoc(doc(db, "chuVuon", id)))),
      Promise.all(maVuonList.map((id) => getDoc(doc(db, "vuon", id)))),
      Promise.all(maLoaiBoList.map((id) => getDoc(doc(db, "loaiBo", id)))),
      Promise.all(maSotList.map((id) => getDoc(doc(db, "sot", id)))),
      Promise.all(maNhanVienList.map((id) => getDoc(doc(db, "nguoiDung", id)))),
      Promise.all(maHopTacXaList.map((id) => getDoc(doc(db, "hopTacXa", id)))),
    ]);

  const tenChuVuon = Object.fromEntries(chuVuonDocs.map((d) => [d.id, d.data()?.hoTen]));
  const tenVuon = Object.fromEntries(vuonDocs.map((d) => [d.id, d.data()?.tenVuon]));
  const tenLoaiBo = Object.fromEntries(loaiBoDocs.map((d) => [d.id, d.data()?.tenLoai]));
  const maSotHienThi = Object.fromEntries(sotDocs.map((d) => [d.id, d.data()?.maSot]));
  const tenNhanVien = Object.fromEntries(
    nhanVienDocs.map((d) => [d.id, d.data()?.hoTen ?? d.data()?.email])
  );
  const tenHopTacXa = Object.fromEntries(hopTacXaDocs.map((d) => [d.id, d.data()?.tenHopTacXa]));

  return dsGiaoDich.map((gd) => ({
    ...gd,
    tenChuVuon: tenChuVuon[gd.maChuVuon] ?? null,
    tenVuon: tenVuon[gd.maVuon] ?? null,
    // Sọt đầu tiên của PO — back-compat, mọi nơi hiện chỉ hiển thị/link 1
    // Sọt (XacNhanThanhToan, giao-dich-cua-toi, trang-chu...) tiếp tục dùng
    // được field này không cần sửa gì.
    maSotHienThi: maSotHienThi[gd.maSot] ?? null,
    // Mã hiển thị của MỌI Sọt PO này có chạm tới, cùng thứ tự với
    // gd.danhSachSot (index i của mảng này ứng với index i của
    // danhSachSot) — dùng khi PO trải >1 Sọt cần liệt kê đủ.
    danhSachSotHienThi: (gd.danhSachSot ?? []).map((ms) => maSotHienThi[ms] ?? null),
    tenHopTacXa: gd.maHopTacXa ? tenHopTacXa[gd.maHopTacXa] ?? null : null,
    ...(kemNhanVien ? { tenNhanVien: tenNhanVien[gd.maNhanVien] ?? null } : {}),
    items: gd.items.map((it) => ({
      ...it,
      tenLoaiBo: tenLoaiBo[it.maLoaiBo] ?? null,
      // Mã hiển thị của ĐÚNG Sọt dòng này thuộc về — tiện cho UI hiện badge
      // "Sọt: <mã>" trên từng dòng khi PO trải nhiều Sọt, không cần frontend
      // tự zip lại 2 mảng danhSachSot/danhSachSotHienThi theo item.
      maSotHienThi: maSotHienThi[it.maSot] ?? null,
    })),
  }));
}

/**
 * Lọc PO gắn đúng Sọt (+ chu kỳ hiện tại nếu có) — cùng logic với
 * kiemTraThanhToanTruocNhapKho / tomTatThuMuaTheoSot để UI kho và chặn
 * nhập kho không lệch nhau.
 */
function locGiaoDichTheoSotChuKy(ds, maSot, chuKyId) {
  return ds.filter((gd) => {
    const ganSot = gd.maSot === maSot || (gd.danhSachSot || []).includes(maSot);
    if (!ganSot) return false;
    if (!chuKyId) return true;
    if (gd.chuKyId === chuKyId) return true;
    if ((gd.danhSachChuKy || []).includes(chuKyId)) return true;
    if ((gd.items || []).some((it) => it.maSot === maSot && it.chuKyId === chuKyId)) {
      return true;
    }
    return false;
  });
}

/** Kg thuộc đúng Sọt (+ chu kỳ) trong 1 PO — bỏ dòng cân của Sọt/chu kỳ khác. */
function tongKgTheoSotChuKy(gd, maSot, chuKyId) {
  return (gd.items || []).reduce((s, it) => {
    if (it.maSot !== maSot) return s;
    if (chuKyId && it.chuKyId && it.chuKyId !== chuKyId) return s;
    return s + Number(it.khoiLuong || 0);
  }, 0);
}

export const giaoDichApi = {
  // Một lượt thu mua (PO) có thể gồm nhiều loại bơ VÀ trải nhiều Sọt
  // khác nhau — khi 1 Sọt đầy giữa chừng lúc đang cân, nhân viên quét Sọt mới
  // ngay tại form, các dòng cân sau đó gắn sang Sọt mới đó
  // (xem items[].maSot). Nhận items[] đã có sẵn maSot mỗi dòng (do
  // frontend gán theo Sọt đang active lúc cân dòng đó). Tạo trong 1
  // transaction, đọc + cộng dồn khối lượng cho MỌI Sọt phân biệt mà PO
  // này chạm tới, để hoặc ghi trọn vẹn, hoặc không ghi gì cả nếu lỗi giữa
  // chừng.
  async taoMoi({ maChuVuon, maVuon, maHopTacXa, hinhThucTt, ghiChu, items }) {
    if (!maChuVuon || !maVuon || !items?.length) {
      throw new Error("Thiếu thông tin giao dịch");
    }
    kiemTraDongItems(items);

    const itemsMoi = tinhItems(items);
    const theoSot = gomTheoSot(itemsMoi);
    const dsMaSot = [...theoSot.keys()];

    return runTransaction(db, async (tx) => {
      // Transaction Firestore bắt buộc mọi tx.get() đứng trước tx.set/update
      // — đọc hết mọi Sọt phân biệt trước khi tính/ghi gì cả.
      const sotDocs = await Promise.all(dsMaSot.map((maSot) => tx.get(doc(db, "sot", maSot))));

      const chuKyTheoSot = {};
      const capNhatSot = [];
      dsMaSot.forEach((maSot, idx) => {
        const sotDoc = sotDocs[idx];
        if (!sotDoc.exists()) throw new Error("Không tìm thấy Sọt");

        const sot = sotDoc.data();
        if (TRANG_THAI_KHOA.includes(sot.trangThai)) {
          throw new Error(`Sọt ${sot.maSot ?? maSot} đã đóng, không thể thu mua thêm`);
        }

        const khoiLuongThem = tongKhoiLuong(theoSot.get(maSot));
        const khoiLuongHienTai = (sot.khoiLuongHienTai ?? 0) + khoiLuongThem;
        chuanVuotToiDa(sot, khoiLuongHienTai);
        const trangThai = tinhTrangThaiTiepTheo(
          khoiLuongHienTai,
          sot.khoiLuongToiDa,
          sot.trangThai
        );
        // 1 chu kỳ Sọt = từ lượt thu mua đầu tiên (Sọt còn trống, chưa
        // có mã) tới lúc xuất kho (sotApi.xuatKho xoá mã này) — sinh mã
        // mới đúng 1 lần khi bắt đầu chu kỳ, các lượt thu mua sau đó trong
        // cùng chu kỳ (nhiều PO trên cùng Sọt trước khi đầy) dùng lại
        // đúng mã cũ.
        const chuKyHienTai = sot.chuKyHienTai ?? lichSuKhoApi.sinhChuKyId();
        chuKyTheoSot[maSot] = chuKyHienTai;

        capNhatSot.push({
          ref: doc(db, "sot", maSot),
          patch: {
            khoiLuongHienTai,
            // "Số giao dịch" trên Sọt đếm số PO (lượt thu mua) từng chạm
            // tới NÓ, không phải số dòng loại bơ bên trong — 1 PO nhiều loại
            // bơ trên cùng 1 Sọt vẫn tính là 1; PO trải N Sọt thì mỗi Sọt
            // +1 riêng.
            soGiaoDich: increment(1),
            trangThai,
            chuKyHienTai,
          },
        });
      });

      const itemsCuoiCung = itemsMoi.map((it) => ({
        ...it,
        chuKyId: chuKyTheoSot[it.maSot],
      }));

      const giaoDichRef = doc(giaoDichCol());
      const data = {
        // Sọt/chu kỳ của item ĐẦU TIÊN — back-compat cho mọi nơi hiện chỉ
        // hiển thị/link đúng 1 Sọt (xem ganTenLienQuan).
        maSot: itemsCuoiCung[0].maSot,
        chuKyId: itemsCuoiCung[0].chuKyId,
        // Khử trùng mọi Sọt/chu kỳ PO này chạm tới — phục vụ query
        // array-contains (theoSot/theoChuKy) và hiển thị "PO gồm N Sọt".
        danhSachSot: dsMaSot,
        danhSachChuKy: [...new Set(dsMaSot.map((ms) => chuKyTheoSot[ms]))],
        maChuVuon,
        maVuon,
        maHopTacXa: maHopTacXa || null,
        maNhanVien: auth.currentUser?.uid ?? null,
        items: itemsCuoiCung,
        hinhThucTt: hinhThucTt ?? "tien_mat",
        ghiChu: ghiChu ?? null,
        thoiGian: new Date().toISOString(),
        // Luồng thanh toán qua kế toán: cho_xu_ly → cho_ke_toan (NV gửi) →
        // dang_thanh_toan (KT nhận) → da_thanh_toan (KT + chứng từ). Xem
        // guiKeToan / nhanThongTinThanhToan / xacNhanDaThanhToan và
        // lib/nhanTrangThaiThanhToan.
        trangThaiThanhToan: "cho_xu_ly",
      };
      tx.set(giaoDichRef, data);

      capNhatSot.forEach(({ ref, patch }) => tx.update(ref, patch));

      return { id: giaoDichRef.id, ...data };
    });
  },

  async chiTiet(id) {
    const snap = await getDoc(doc(db, "giaoDich", id));
    if (!snap.exists()) throw new Error("Không tìm thấy giao dịch");
    const [ganTen] = await ganTenLienQuan([{ id: snap.id, ...snap.data() }], {
      kemNhanVien: true,
    });
    return ganTen;
  },

  // Helper nội bộ — chỉ whitelist FIELD_THANH_TOAN. UI nên gọi các hàm
  // transition bên dưới (guiKeToan / nhanThongTinThanhToan / traLaiChoXuLy /
  // xacNhanDaThanhToan) thay vì tự ghép patch trạng thái.
  async capNhatThanhToan(id, patch) {
    const loaiBo = Object.keys(patch).filter((k) => !FIELD_THANH_TOAN.includes(k));
    if (loaiBo.length > 0) {
      throw new Error(`Không được phép sửa field: ${loaiBo.join(", ")}`);
    }
    await updateDoc(doc(db, "giaoDich", id), patch);
  },

  // NV tạo PO: cho_xu_ly → cho_ke_toan. Chỉ đúng người tạo (rules cũng
  // siết maNhanVien == uid, admin không gửi giúp được).
  async guiKeToan(id) {
    const ref = doc(db, "giaoDich", id);
    const snap = await getDoc(ref);
    if (!snap.exists()) throw new Error("Không tìm thấy giao dịch");
    const gd = snap.data();
    if (gd.trangThaiThanhToan !== "cho_xu_ly") {
      throw new Error("Chỉ gửi kế toán khi giao dịch còn chờ xử lý");
    }
    const uid = auth.currentUser?.uid;
    if (!uid || gd.maNhanVien !== uid) {
      throw new Error("Chỉ nhân viên tạo phiếu mới được gửi về kế toán");
    }
    await updateDoc(ref, {
      trangThaiThanhToan: "cho_ke_toan",
      // Xoá ghi chú trả về cũ của KT khi NV gửi lại sau khi sửa.
      ghiChuThanhToan: null,
    });
  },

  // NV tạo phiếu: chuông nhắc KT khi còn cho_ke_toan. Cooldown 30s phía client.
  // Field thoiGianChuongKeToan — KT listener phát âm thanh chuông khi đổi.
  kiemTraDaChuong,

  async chuongNhacKeToan(id) {
    const ref = doc(db, "giaoDich", id);
    const snap = await getDoc(ref);
    if (!snap.exists()) throw new Error("Không tìm thấy giao dịch");
    const gd = snap.data();
    if (gd.trangThaiThanhToan !== "cho_ke_toan") {
      throw new Error("Chỉ chuông nhắc khi phiếu đang chờ kế toán");
    }
    const uid = auth.currentUser?.uid;
    if (!uid || gd.maNhanVien !== uid) {
      throw new Error("Chỉ nhân viên tạo phiếu mới được chuông nhắc kế toán");
    }
    const check = kiemTraDaChuong(gd.thoiGianChuongKeToan);
    if (!check.ok) throw new Error(check.message);
    const thoiGianChuongKeToan = new Date().toISOString();
    await updateDoc(ref, { thoiGianChuongKeToan });
    return { thoiGianChuongKeToan };
  },

  // KT/admin: cho_ke_toan → dang_thanh_toan (+ người nhận / thời gian).
  async nhanThongTinThanhToan(id) {
    const ref = doc(db, "giaoDich", id);
    const snap = await getDoc(ref);
    if (!snap.exists()) throw new Error("Không tìm thấy giao dịch");
    if (snap.data().trangThaiThanhToan !== "cho_ke_toan") {
      throw new Error("Chỉ nhận thông tin khi giao dịch đang chờ kế toán");
    }
    await updateDoc(ref, {
      trangThaiThanhToan: "dang_thanh_toan",
      nguoiNhanThongTin: auth.currentUser?.uid ?? null,
      thoiGianNhanThongTin: new Date().toISOString(),
    });
  },

  // KT/admin: cho_ke_toan|dang_thanh_toan → cho_xu_ly. Ghi chú bắt buộc để
  // NV biết cần sửa gì; xoá dấu vết nhận tin / chứng từ nháp nếu có.
  async traLaiChoXuLy(id, { ghiChuThanhToan } = {}) {
    const ghiChu = typeof ghiChuThanhToan === "string" ? ghiChuThanhToan.trim() : "";
    if (!ghiChu) throw new Error("Bắt buộc nhập ghi chú khi trả lại");

    const ref = doc(db, "giaoDich", id);
    const snap = await getDoc(ref);
    if (!snap.exists()) throw new Error("Không tìm thấy giao dịch");
    const tt = snap.data().trangThaiThanhToan;
    if (tt !== "cho_ke_toan" && tt !== "dang_thanh_toan") {
      throw new Error("Chỉ trả lại khi đang chờ kế toán hoặc đang thanh toán");
    }
    await updateDoc(ref, {
      trangThaiThanhToan: "cho_xu_ly",
      ghiChuThanhToan: ghiChu,
      nguoiNhanThongTin: null,
      thoiGianNhanThongTin: null,
      chungTuUrl: null,
      nguoiXuLyThanhToan: null,
      thoiGianThanhToan: null,
    });
  },

  // KT/admin: dang_thanh_toan → da_thanh_toan. chungTuUrl bắt buộc (rules
  // cũng kiểm size() > 0). NV không còn tự xác nhận thanh toán như cũ.
  async xacNhanDaThanhToan(id, { chungTuUrl, ghiChuThanhToan } = {}) {
    const url = typeof chungTuUrl === "string" ? chungTuUrl.trim() : "";
    if (!url) throw new Error("Bắt buộc đính kèm chứng từ trước khi xác nhận đã thanh toán");

    const ref = doc(db, "giaoDich", id);
    const snap = await getDoc(ref);
    if (!snap.exists()) throw new Error("Không tìm thấy giao dịch");
    if (snap.data().trangThaiThanhToan !== "dang_thanh_toan") {
      throw new Error("Chỉ xác nhận đã thanh toán khi đang ở trạng thái đang thanh toán");
    }

    const patch = {
      trangThaiThanhToan: "da_thanh_toan",
      chungTuUrl: url,
      nguoiXuLyThanhToan: auth.currentUser?.uid ?? null,
      thoiGianThanhToan: new Date().toISOString(),
    };
    if (ghiChuThanhToan !== undefined) {
      patch.ghiChuThanhToan =
        typeof ghiChuThanhToan === "string" && ghiChuThanhToan.trim()
          ? ghiChuThanhToan.trim()
          : null;
    }
    await updateDoc(ref, patch);
  },

  // Hàng chờ kế toán: PO ở cho_ke_toan hoặc dang_thanh_toan.
  // Query `in` trên 1 field — Firestore tự tạo single-field index, không cần
  // composite. Sort thoiGian phía client (giống cuaToi/theoChuVuon).
  async hangChoKeToan() {
    const snap = await getDocs(
      query(giaoDichCol(), where("trangThaiThanhToan", "in", TRANG_THAI_HANG_CHO_KE_TOAN))
    );
    const ketQua = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    const ganTen = await ganTenLienQuan(ketQua, { kemNhanVien: true });
    return ganTen.sort((a, b) => (a.thoiGian < b.thoiGian ? 1 : -1));
  },

  // Realtime hàng chờ — NV gửi kế toán là KT thấy liền (không cần bấm Tải).
  // Trả về hàm hủy lắng nghe (unsubscribe). onChange nhận mảng đã ganTen + sort.
  langNgheHangChoKeToan(onChange, onError) {
    return langNgheFirestore(
      query(giaoDichCol(), where("trangThaiThanhToan", "in", TRANG_THAI_HANG_CHO_KE_TOAN)),
      {
        mapAsync: async (raw) => {
          const ganTen = await ganTenLienQuan(raw ?? [], { kemNhanVien: true });
          return ganTen.sort((a, b) => (a.thoiGian < b.thoiGian ? 1 : -1));
        },
        onChange,
        onError,
      }
    );
  },

  /**
   * Listener nhẹ cho âm thanh KT — không ganTen (tránh N+1 getDoc mỗi snapshot).
   * Chỉ trả id / trangThaiThanhToan / maNhanVien / thoiGianChuongKeToan.
   */
  langNgheTrangThaiHangChoKeToan(onChange, onError) {
    return langNgheFirestore(
      query(giaoDichCol(), where("trangThaiThanhToan", "in", TRANG_THAI_HANG_CHO_KE_TOAN)),
      {
        mapAsync: (raw) =>
          (raw ?? []).map((po) => ({
            id: po.id,
            trangThaiThanhToan: po.trangThaiThanhToan,
            maNhanVien: po.maNhanVien ?? null,
            thoiGianChuongKeToan: po.thoiGianChuongKeToan ?? null,
          })),
        onChange,
        onError,
      }
    );
  },

  // Lịch sử đã thanh toán (realtime) — lọc khoảng ngày phía server theo
  // thoiGianThanhToan (cần composite index trangThaiThanhToan + thoiGianThanhToan).
  // Fallback client theo thoiGian chỉ còn cho biên ngày (timezone / chuỗi).
  langNgheDaThanhToan(tuNgay, denNgay, onChange, onError) {
    if (!tuNgay || !denNgay) {
      onChange([]);
      return () => {};
    }
    const tu = `${tuNgay}T00:00:00.000Z`;
    const den = `${denNgay}T23:59:59.999Z`;
    return langNgheFirestore(
      query(
        giaoDichCol(),
        where("trangThaiThanhToan", "==", "da_thanh_toan"),
        where("thoiGianThanhToan", ">=", tu),
        where("thoiGianThanhToan", "<=", den)
      ),
      {
        mapAsync: async (raw) => {
          const ketQua = (raw ?? []).filter((gd) => {
            const ngay = (gd.thoiGianThanhToan || gd.thoiGian || "").slice(0, 10);
            return ngay >= tuNgay && ngay <= denNgay;
          });
          const ganTen = await ganTenLienQuan(ketQua, { kemNhanVien: true });
          return ganTen.sort((a, b) => {
            const ta = a.thoiGianThanhToan || a.thoiGian || "";
            const tb = b.thoiGianThanhToan || b.thoiGian || "";
            return ta < tb ? 1 : -1;
          });
        },
        onChange,
        onError,
      }
    );
  },

  // Realtime 1 phiếu — trang chi tiết KT cập nhật khi trạng thái đổi.
  langNgheChiTiet(id, onChange, onError) {
    if (!id) {
      onChange(null);
      return () => {};
    }
    return langNgheFirestore(doc(db, "giaoDich", id), {
      mapAsync: async (raw) => {
        if (!raw) return null;
        const [ganTen] = await ganTenLienQuan([raw], { kemNhanVien: true });
        return ganTen;
      },
      onChange,
      onError,
    });
  },

  // Realtime "Giao dịch của tôi" — lọc ngày phía client (giống cuaToi).
  langNgheCuaToi(ngay, onChange, onError) {
    const uid = auth.currentUser?.uid;
    if (!uid) {
      onChange([]);
      return () => {};
    }
    return langNgheFirestore(query(giaoDichCol(), where("maNhanVien", "==", uid)), {
      mapAsync: async (raw) => {
        let ketQua = raw ?? [];
        if (ngay) {
          ketQua = ketQua.filter((gd) => gd.thoiGian?.slice(0, 10) === ngay);
        }
        const ganTen = await ganTenLienQuan(ketQua);
        return ganTen.sort((a, b) => (a.thoiGian < b.thoiGian ? 1 : -1));
      },
      onChange,
      onError,
    });
  },

  /**
   * Listener nhẹ cho âm thanh NV — không ganTen (tránh N+1 getDoc mỗi snapshot).
   * Truyền uid từ AuthContext (không đọc auth.currentUser lúc gọi — tránh race).
   */
  langNgheTrangThaiCuaToi(uid, onChange, onError) {
    if (!uid) {
      onChange([]);
      return () => {};
    }
    return langNgheFirestore(query(giaoDichCol(), where("maNhanVien", "==", uid)), {
      mapAsync: (raw) =>
        (raw ?? []).map((po) => ({
          id: po.id,
          trangThaiThanhToan: po.trangThaiThanhToan,
        })),
      onChange,
      onError,
    });
  },

  langNgheTrongKhoang(tuNgay, denNgay, onChange, onError) {
    if (!tuNgay || !denNgay) {
      onChange([]);
      return () => {};
    }
    const tu = `${tuNgay}T00:00:00.000Z`;
    const den = `${denNgay}T23:59:59.999Z`;
    return langNgheFirestore(
      query(giaoDichCol(), where("thoiGian", ">=", tu), where("thoiGian", "<=", den)),
      {
        mapAsync: async (raw) => {
          const ganTen = await ganTenLienQuan(raw ?? [], { kemNhanVien: true });
          return ganTen.sort((a, b) => (a.thoiGian < b.thoiGian ? 1 : -1));
        },
        onChange,
        onError,
      }
    );
  },

  langNgheTheoChuVuon(maChuVuon, onChange, onError) {
    if (!maChuVuon) {
      onChange([]);
      return () => {};
    }
    return langNgheFirestore(query(giaoDichCol(), where("maChuVuon", "==", maChuVuon)), {
      mapAsync: async (raw) => {
        const ganTen = await ganTenLienQuan(raw ?? []);
        return ganTen.sort((a, b) => (a.thoiGian < b.thoiGian ? 1 : -1));
      },
      onChange,
      onError,
    });
  },

  langNgheTheoSot(maSot, onChange, onError) {
    if (!maSot) {
      onChange([]);
      return () => {};
    }
    return langNgheFirestore(
      query(
        giaoDichCol(),
        or(where("maSot", "==", maSot), where("danhSachSot", "array-contains", maSot))
      ),
      {
        mapAsync: (raw) => ganTenLienQuan(raw ?? []),
        onChange,
        onError,
      }
    );
  },

  /**
   * Chặn nhập kho khi còn phiếu chưa da_thanh_toan trên Sọt (chu kỳ hiện tại).
   * Trả về { soPhieu, poIds } để nhapKho đọc lại từng PO trong transaction.
   */
  async kiemTraThanhToanTruocNhapKho(maSot, chuKyId) {
    const snap = await getDocs(
      query(
        giaoDichCol(),
        or(where("maSot", "==", maSot), where("danhSachSot", "array-contains", maSot))
      )
    );
    const ds = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    const lienQuan = locGiaoDichTheoSotChuKy(ds, maSot, chuKyId);

    if (lienQuan.length === 0) {
      throw new Error("Không tìm thấy phiếu thu mua gắn Sọt này — không thể nhập kho.");
    }

    const chuaTt = lienQuan.filter((gd) => gd.trangThaiThanhToan !== "da_thanh_toan");
    if (chuaTt.length > 0) {
      throw new Error(
        `Còn ${chuaTt.length}/${lienQuan.length} phiếu chưa được kế toán thanh toán — không thể nhập kho.`
      );
    }
    return { soPhieu: lienQuan.length, poIds: lienQuan.map((gd) => gd.id) };
  },

  /**
   * Tóm tắt thu mua gắn Sọt (chu kỳ hiện tại nếu có) — shape gọn cho UI
   * Nhập/Xuất kho lúc quét QR: farm = chủ vườn/vườn, danh sách phiếu + kg.
   * Không ném khi chưa có phiếu (trả soPhieu=0); khác kiemTraThanhToanTruocNhapKho.
   */
  async tomTatThuMuaTheoSot(maSot, chuKyId) {
    if (!maSot) {
      return { soPhieu: 0, tongKg: 0, farms: [], phieu: [] };
    }
    const snap = await getDocs(
      query(
        giaoDichCol(),
        or(where("maSot", "==", maSot), where("danhSachSot", "array-contains", maSot))
      )
    );
    const ketQua = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    // Cần tenNhanVien cho card kho — theoSot() mặc định không gắn.
    const ganTen = await ganTenLienQuan(ketQua, { kemNhanVien: true });
    const lienQuan = locGiaoDichTheoSotChuKy(ganTen, maSot, chuKyId ?? null).sort((a, b) =>
      a.thoiGian < b.thoiGian ? 1 : -1
    );

    const phieu = lienQuan.map((gd) => ({
      id: gd.id,
      tenChuVuon: gd.tenChuVuon ?? null,
      tenVuon: gd.tenVuon ?? null,
      tenNhanVien: gd.tenNhanVien ?? null,
      tongKg: tongKgTheoSotChuKy(gd, maSot, chuKyId ?? null),
      trangThaiThanhToan: gd.trangThaiThanhToan ?? null,
      thoiGian: gd.thoiGian ?? null,
      chuKyId: gd.chuKyId ?? null,
    }));

    const farmMap = new Map();
    phieu.forEach((p, idx) => {
      const gd = lienQuan[idx];
      const key = `${gd.maChuVuon ?? ""}|${gd.maVuon ?? ""}`;
      const hienTai = farmMap.get(key) ?? {
        tenChuVuon: p.tenChuVuon,
        tenVuon: p.tenVuon,
        soPhieu: 0,
        tongKg: 0,
      };
      hienTai.soPhieu += 1;
      hienTai.tongKg += p.tongKg;
      farmMap.set(key, hienTai);
    });

    const farms = [...farmMap.values()].sort((a, b) =>
      (a.tenChuVuon || "").localeCompare(b.tenChuVuon || "", "vi")
    );
    const tongKg = phieu.reduce((s, p) => s + p.tongKg, 0);

    return {
      soPhieu: phieu.length,
      tongKg,
      farms,
      phieu,
    };
  },

  // Sửa cả PO (thêm/sửa/xoá dòng loại bơ, kể cả đổi/thêm Sọt) khi còn
  // "cho_xu_ly" (chưa thanh toán) — luôn ghi đè nguyên mảng items, không cần
  // biết dòng nào cũ/mới. Vì items có thể trải nhiều Sọt khác nhau (cũ
  // lẫn mới), gom cả 2 phía theo maSot rồi tính delta khối lượng RIÊNG
  // từng Sọt (hợp của mọi Sọt xuất hiện ở ít nhất 1 phía), thay vì giả
  // định cả PO chỉ đụng đúng 1 Sọt như trước — tất cả trong 1 transaction.
  async suaPO(id, { items, ghiChu, maHopTacXa, hinhThucTt }) {
    if (!items?.length) throw new Error("PO phải có ít nhất 1 loại bơ");
    kiemTraDongItems(items);

    const giaoDichRef = doc(db, "giaoDich", id);
    const itemsMoi = tinhItems(items);
    const theoSotMoi = gomTheoSot(itemsMoi);

    return runTransaction(db, async (tx) => {
      const gdDoc = await tx.get(giaoDichRef);
      if (!gdDoc.exists()) throw new Error("Không tìm thấy giao dịch");
      const gd = chuanHoaItems({ id: gdDoc.id, ...gdDoc.data() });
      if (gd.trangThaiThanhToan !== "cho_xu_ly") {
        throw new Error("Giao dịch đã được xử lý thanh toán, không thể sửa nữa");
      }

      const theoSotCu = gomTheoSot(gd.items);
      const dsMaSot = [...new Set([...theoSotCu.keys(), ...theoSotMoi.keys()])];

      // Đọc hết mọi Sọt (cũ + mới) trước khi ghi bất kỳ gì — bắt buộc của
      // transaction Firestore.
      const sotDocs = await Promise.all(dsMaSot.map((maSot) => tx.get(doc(db, "sot", maSot))));

      const chuKyTheoSot = {};
      const capNhatSot = [];
      dsMaSot.forEach((maSot, idx) => {
        const sotDoc = sotDocs[idx];
        const laSotMoiThem = !theoSotCu.has(maSot) && theoSotMoi.has(maSot);
        const laSotBiBoHan = theoSotCu.has(maSot) && !theoSotMoi.has(maSot);

        if (!sotDoc.exists()) {
          // Sọt chỉ còn ở items CŨ nhưng doc đã bị xoá khỏi hệ thống —
          // không có gì để cập nhật số liệu, vẫn cho sửa PO (lỗi dữ liệu đã
          // tồn tại từ trước, không phải do thao tác này gây ra). Nếu items
          // MỚI lại quét đúng ID đó thì chặn hẳn vì không thể thu mua vào 1
          // Sọt không tồn tại.
          if (theoSotMoi.has(maSot)) throw new Error("Không tìm thấy Sọt");
          return;
        }

        const sot = sotDoc.data();
        const khoiLuongCu = tongKhoiLuong(theoSotCu.get(maSot) ?? []);
        const khoiLuongMoiSot = tongKhoiLuong(theoSotMoi.get(maSot) ?? []);
        const delta = khoiLuongMoiSot - khoiLuongCu;

        // Chặn TĂNG khối lượng trên Sọt đã khoá — dù là Sọt MỚI thêm vào
        // PO hay Sọt đã có sẵn từ trước nhưng đã bị khoá sau khi PO tạo (vd.
        // kho đã cân/nhập kho Sọt đó trong lúc PO còn "cho_xu_ly"). Trước đây
        // chỉ chặn Sọt mới, nên sửa PO tăng cân trên Sọt cũ đã khoá vẫn lọt.
        if (delta > 0 && TRANG_THAI_KHOA.includes(sot.trangThai)) {
          throw new Error(`Sọt ${sot.maSot ?? maSot} đã đóng, không thể thu mua thêm`);
        }

        // Luôn có fallback sinh chu kỳ mới khi Sọt chưa có (hoặc đã bị
        // xuatKho xoá) chuKyHienTai — giống taoMoi. Thiếu bước này, sửa PO
        // trên 1 Sọt đã qua 1 vòng xuất kho sẽ ghi chuKyId null/undefined
        // vào giaoDich, cắt đứt liên kết chu kỳ (PO biến mất khỏi Chứng từ
        // thu mua) hoặc làm tx.update throw với field undefined.
        const canSuaChuKy = theoSotMoi.has(maSot) && sot.chuKyHienTai == null;
        const chuKyHienTai = sot.chuKyHienTai ?? lichSuKhoApi.sinhChuKyId();
        if (theoSotMoi.has(maSot)) chuKyTheoSot[maSot] = chuKyHienTai;

        // Sọt không đổi khối lượng ở PO này (dòng gán Sọt đó chỉ đổi giá
        // trị khác, hoặc Sọt đã có mặt cả 2 phía với đúng tổng kg) và
        // không cần vá lại chuKyHienTai — khỏi cần ghi update Sọt.
        if (delta === 0 && !canSuaChuKy) return;

        const khoiLuongHienTaiMoi = (sot.khoiLuongHienTai ?? 0) + delta;
        if (khoiLuongHienTaiMoi < 0) {
          throw new Error("Khối lượng mới làm âm khối lượng hiện tại của Sọt");
        }
        if (delta > 0) chuanVuotToiDa(sot, khoiLuongHienTaiMoi);
        const trangThaiSotMoi = tinhTrangThaiTiepTheo(
          khoiLuongHienTaiMoi,
          sot.khoiLuongToiDa,
          sot.trangThai
        );

        const patch = { khoiLuongHienTai: khoiLuongHienTaiMoi, trangThai: trangThaiSotMoi };
        if (laSotMoiThem || canSuaChuKy) {
          // Sọt mới quét lúc sửa (chưa từng thuộc PO này), hoặc Sọt cũ đang
          // thiếu chuKyHienTai cần vá lại — ghi chu kỳ vừa sinh/lấy được vào
          // đúng doc Sọt để lần sửa PO sau không phải sinh lại nữa.
          patch.chuKyHienTai = chuKyHienTai;
        }
        if (laSotMoiThem) {
          // Sọt mới quét lúc sửa (chưa từng thuộc PO này) — coi như taoMoi
          // đang thêm 1 lượt thu mua vào Sọt đó.
          patch.soGiaoDich = increment(1);
        }
        if (laSotBiBoHan) {
          // PO này không còn dòng nào gắn Sọt đó nữa (đổi hết sang Sọt
          // khác) — coi như xoaPO đang gỡ lượt thu mua khỏi Sọt đó.
          patch.soGiaoDich = increment(-1);
          if (khoiLuongHienTaiMoi === 0) patch.chuKyHienTai = null;
        }
        capNhatSot.push({ ref: doc(db, "sot", maSot), patch });
      });

      const itemsCuoiCung = itemsMoi.map((it) => ({
        ...it,
        chuKyId: chuKyTheoSot[it.maSot],
      }));
      const danhSachSot = [...theoSotMoi.keys()];
      const danhSachChuKy = [...new Set(danhSachSot.map((ms) => chuKyTheoSot[ms]))];

      // Chỉ ghi tên field MỚI — whitelist hasOnly([...]) trong firestore.rules
      // (nhánh "cho_xu_ly") phải khớp đúng danh sách này, thêm field ở đây mà
      // quên sửa rules là "Lưu thay đổi" báo permission-denied âm thầm.
      tx.update(giaoDichRef, {
        items: itemsCuoiCung,
        ghiChu: ghiChu ?? null,
        maHopTacXa: maHopTacXa || null,
        ...(hinhThucTt != null ? { hinhThucTt } : {}),
        maSot: itemsCuoiCung[0].maSot,
        chuKyId: itemsCuoiCung[0].chuKyId,
        danhSachSot,
        danhSachChuKy,
      });

      capNhatSot.forEach(({ ref, patch }) => tx.update(ref, patch));
    });
  },

  // Xoá hẳn cả PO khi còn "cho_xu_ly" — dùng khi quay lại sửa PO và người
  // dùng muốn bỏ toàn bộ lượt thu mua này (không phải bỏ 1 dòng, xem suaPO).
  // Gỡ khối lượng khỏi MỌI Sọt phân biệt mà PO này từng chạm tới (có thể
  // >1 nếu PO trải nhiều Sọt).
  async xoaPO(id) {
    const giaoDichRef = doc(db, "giaoDich", id);

    return runTransaction(db, async (tx) => {
      const gdDoc = await tx.get(giaoDichRef);
      if (!gdDoc.exists()) return;
      const gd = chuanHoaItems({ id: gdDoc.id, ...gdDoc.data() });
      if (gd.trangThaiThanhToan !== "cho_xu_ly") {
        throw new Error("Giao dịch đã được xử lý thanh toán, không thể xoá nữa");
      }

      const theoSot = gomTheoSot(gd.items);
      const dsMaSot = [...theoSot.keys()];
      const sotDocs = await Promise.all(dsMaSot.map((maSot) => tx.get(doc(db, "sot", maSot))));

      dsMaSot.forEach((maSot, idx) => {
        const sotDoc = sotDocs[idx];
        if (!sotDoc.exists()) return;

        const sot = sotDoc.data();
        const khoiLuongHienTaiMoi = (sot.khoiLuongHienTai ?? 0) - tongKhoiLuong(theoSot.get(maSot));
        if (khoiLuongHienTaiMoi < -1e-6) {
          throw new Error(
            `Khối lượng Sọt ${
              sot.maSot ?? maSot
            } không khớp phiếu — không xoá được. Tải lại rồi thử lại.`
          );
        }
        const kgSot = Math.max(0, khoiLuongHienTaiMoi);
        const trangThaiSotMoi = tinhTrangThaiTiepTheo(kgSot, sot.khoiLuongToiDa, sot.trangThai);
        tx.update(doc(db, "sot", maSot), {
          khoiLuongHienTai: kgSot,
          soGiaoDich: increment(-1),
          trangThai: trangThaiSotMoi,
          // Xoá PO cuối cùng làm Sọt về lại trống — coi như chu kỳ chưa
          // từng bắt đầu, huỷ mã cũ để lượt thu mua kế tiếp sinh mã mới,
          // tránh gộp nhầm 2 chu kỳ không liên quan vào cùng 1 mã.
          ...(kgSot === 0 ? { chuKyHienTai: null } : {}),
        });
      });

      tx.delete(giaoDichRef);
    });
  },

  async cuaToi(ngay) {
    const uid = auth.currentUser?.uid;
    const snap = await getDocs(query(giaoDichCol(), where("maNhanVien", "==", uid)));
    let ketQua = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

    if (ngay) {
      ketQua = ketQua.filter((gd) => gd.thoiGian?.slice(0, 10) === ngay);
    }

    const ganTen = await ganTenLienQuan(ketQua);
    return ganTen.sort((a, b) => (a.thoiGian < b.thoiGian ? 1 : -1));
  },

  // PO nào từng chạm tới Sọt này — kể cả khi Sọt này chỉ là 1 trong nhiều Sọt
  // của PO đó (không phải Sọt đầu tiên). Doc CŨ (trước tính năng multi-Sọt)
  // chỉ có field top-level maSot, không có mảng danhSachSot; doc MỚI có
  // cả 2. or() gộp đúng cả 2 hình dạng trong 1 lượt query.
  async theoSot(maSot) {
    const snap = await getDocs(
      query(
        giaoDichCol(),
        or(where("maSot", "==", maSot), where("danhSachSot", "array-contains", maSot))
      )
    );
    const ketQua = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    return ganTenLienQuan(ketQua);
  },

  async theoChuVuon(maChuVuon) {
    const snap = await getDocs(query(giaoDichCol(), where("maChuVuon", "==", maChuVuon)));
    const ketQua = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    const ganTen = await ganTenLienQuan(ketQua);
    return ganTen.sort((a, b) => (a.thoiGian < b.thoiGian ? 1 : -1));
  },

  // Toàn bộ PO thuộc đúng 1 chu kỳ Sọt (từ lượt thu mua đầu tới lúc nhập
  // kho/xuất kho) — không giới hạn theo ngày, vì 1 chu kỳ có thể trải dài
  // qua nhiều ngày. Dùng để đối chiếu số liệu mua/nhập kho ở trang Chứng từ
  // thu mua (xem lib/tinhChungTuThuMua.js). Cùng lý do or() như theoSot ở
  // trên: gộp cả doc cũ (chỉ field top-level chuKyId) lẫn doc mới (có mảng
  // danhSachChuKy).
  async theoChuKy(chuKyId) {
    if (!chuKyId) return [];
    const map = await this.theoNhieuChuKy([chuKyId]);
    return map[chuKyId] ?? [];
  },

  /**
   * Nhiều chu kỳ cùng lúc — gom PO trùng, ganTenLienQuan 1 lần (tránh N+1
   * getDoc chủ vườn/vườn/sọt lặp lại trên từng theoChuKy).
   * @returns {Object<string, Array>} chuKyId → danh sách PO đã ganTen.
   */
  async theoNhieuChuKy(chuKyIds) {
    const unique = [...new Set((chuKyIds ?? []).filter(Boolean))];
    if (unique.length === 0) return {};

    const poMap = new Map();
    const chuKyToPoIds = Object.fromEntries(unique.map((id) => [id, new Set()]));

    const ganPo = (raw) => {
      const gd = chuanHoaItems(raw);
      if (!poMap.has(gd.id)) poMap.set(gd.id, gd);
      return gd;
    };

    const CHUNK = 30;
    for (let i = 0; i < unique.length; i += CHUNK) {
      const chunk = unique.slice(i, i + CHUNK);
      const snap = await getDocs(query(giaoDichCol(), where("chuKyId", "in", chunk)));
      for (const d of snap.docs) {
        const gd = ganPo({ id: d.id, ...d.data() });
        const cycles = [...new Set([gd.chuKyId, ...(gd.danhSachChuKy ?? [])].filter(Boolean))];
        for (const ck of cycles) {
          if (chuKyToPoIds[ck]) chuKyToPoIds[ck].add(gd.id);
        }
      }
    }

    await Promise.all(
      unique.map(async (chuKyId) => {
        const snap = await getDocs(
          query(giaoDichCol(), where("danhSachChuKy", "array-contains", chuKyId))
        );
        for (const d of snap.docs) {
          const gd = ganPo({ id: d.id, ...d.data() });
          chuKyToPoIds[chuKyId].add(gd.id);
        }
      })
    );

    const ganTenAll = await ganTenLienQuan([...poMap.values()], { kemNhanVien: true });
    const byId = Object.fromEntries(ganTenAll.map((gd) => [gd.id, gd]));

    return Object.fromEntries(
      unique.map((chuKyId) => [
        chuKyId,
        [...chuKyToPoIds[chuKyId]]
          .map((id) => byId[id])
          .filter(Boolean)
          .sort((a, b) => (a.thoiGian < b.thoiGian ? 1 : -1)),
      ])
    );
  },

  // Dùng cho Báo cáo (admin/kế toán): toàn bộ giao dịch trong khoảng ngày,
  // không giới hạn theo nhân viên. thoiGian lưu dạng ISO string nên range
  // query lexicographic là chính xác, không cần composite index.
  async trongKhoang(tuNgay, denNgay) {
    const tu = `${tuNgay}T00:00:00.000Z`;
    const den = `${denNgay}T23:59:59.999Z`;
    const snap = await getDocs(
      query(giaoDichCol(), where("thoiGian", ">=", tu), where("thoiGian", "<=", den))
    );
    const ketQua = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    const ganTen = await ganTenLienQuan(ketQua, { kemNhanVien: true });
    return ganTen.sort((a, b) => (a.thoiGian < b.thoiGian ? 1 : -1));
  },
};
